import { useState } from 'react';
import type { ReactElement } from 'react';
import { Dialog } from '../../components/Dialog.js';
import { IngredientAutocomplete } from './IngredientAutocomplete.js';
import type { Ingredient } from '../../api/types.js';
import { listIngredients } from '../../api/ingredients.js';

interface FridgeSheetProps {
  open: boolean;
  onClose: () => void;
  selected: number[];
  onApply: (ids: number[]) => void;
}

export function FridgeSheet({
  open,
  onClose,
  selected,
  onApply,
}: FridgeSheetProps): ReactElement | null {
  const [draft, setDraft] = useState<number[]>(selected);
  const [known, setKnown] = useState<Ingredient[]>([]);

  if (!open) return null;

  async function load(): Promise<void> {
    try {
      const rows = await listIngredients({});
      setKnown(rows);
    } catch {
      // ignore
    }
  }

  // Lazy-load on first open
  if (open && known.length === 0) {
    void load();
  }

  function add(id: number): void {
    if (draft.includes(id)) return;
    setDraft([...draft, id]);
  }

  function remove(id: number): void {
    setDraft(draft.filter((x) => x !== id));
  }

  return (
    <Dialog open={open} onClose={onClose} title="🥕 What&apos;s in my fridge?">
      <div className="flex flex-col gap-3">
        <IngredientAutocomplete
          value={null}
          onChange={(id) => {
            if (id !== null) add(id);
          }}
          placeholder="Search ingredients to add…"
          testId="fridge-autocomplete"
        />
        <div className="flex flex-wrap gap-1" data-testid="fridge-draft-chips">
          {draft.map((id) => {
            const ing = known.find((k) => k.id === id);
            return (
              <span
                key={id}
                className="flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs"
                style={{
                  borderColor: 'var(--border)',
                  color: 'var(--text-main)',
                  background: 'var(--bg-surface)',
                }}
              >
                {ing ? ing.name : `#${id}`}
                <button
                  type="button"
                  onClick={() => remove(id)}
                  aria-label="Remove"
                  className="flex h-5 w-5 items-center justify-center"
                  style={{ color: 'var(--text-muted)' }}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border px-3 py-2 text-sm"
            style={{ borderColor: 'var(--border)', color: 'var(--text-main)', minHeight: 40 }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              onApply(draft);
              onClose();
            }}
            className="rounded-md border px-3 py-2 text-sm font-medium"
            style={{
              background: 'var(--accent)',
              borderColor: 'var(--accent)',
              color: 'var(--bg-primary, #fff)',
              minHeight: 40,
            }}
            data-testid="fridge-apply"
          >
            Apply
          </button>
        </div>
      </div>
    </Dialog>
  );
}